// ─────────────────────────────────────────────────────────────────────────────
//  Matchmaker Earnings — the platform's side of the money rail.
//
//  The ledger accrues on its own (services/partner/earnings.js derives rows
//  from SUCCESS PaymentOrders). This screen exists for the two questions that
//  decide whether the business runs on Saturday morning:
//
//      1. Who is owed money right now?
//      2. Did anything reverse, and why?
//
//  Design notes that are load-bearing rather than decorative:
//
//  · The headline is a rupee total, not a row count. An operator opening this
//    screen has a payout to make, not a table to browse.
//  · A blocked row still shows what she is owed. Hiding an unpayable
//    matchmaker would hide a liability; what she needs is the amount AND the
//    reason it cannot move yet, so somebody can go fix the KYC.
//  · Recording a payout is not sending one. The copy says so, because a button
//    labelled "Pay" in a panel that cannot move money is how ₹40,000 gets
//    marked settled and never transferred.
//  · The KYC refusal (409) is enforced on the server too — this button being
//    disabled is a courtesy, not the control.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useCallback } from "react";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faIndianRupeeSign,
  faChevronDown,
  faChevronRight,
  faTriangleExclamation,
  faCircleCheck,
  faRotateLeft,
  faFileArrowDown,
  faXmark,
  faTicket,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

const TABS = [
  { key: "pending", label: "Owed now", hint: "Accrued and unpaid" },
  { key: "paid", label: "Settled", hint: "Already transferred" },
  { key: "reversed", label: "Reversed", hint: "The payment behind it failed or refunded" },
];

const inr = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const KYC_CHIP = {
  VERIFIED: "bg-green-100 text-green-700",
  PENDING: "bg-amber-100 text-amber-700",
  REJECTED: "bg-red-100 text-red-600",
  NONE: "bg-gray-100 text-gray-500",
};

const MatchmakerEarnings = () => {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [totalOwed, setTotalOwed] = useState(0);
  const [loading, setLoading] = useState(true);

  // Detail is lazy and cached per matchmaker: the list answers "how much",
  // and only an operator who actually questions a number pays for the join.
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  const [payTarget, setPayTarget] = useState(null);
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (status, signal) => {
    setLoading(true);
    try {
      const res = await func.get(urls.EVENT_EARNINGS_URL, { params: { status }, signal });
      if (res?.status === 200) {
        setRows(res?.data?.matchmakers || []);
        setTotalOwed(res?.data?.totalOwed || 0);
      } else {
        toast.error("Could not load earnings");
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      toast.error("Network error loading earnings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(tab, controller.signal);
    return () => controller.abort();
  }, [tab, load]);

  const toggle = async (mm) => {
    if (expanded === mm.matchmakerId) return setExpanded(null);
    setExpanded(mm.matchmakerId);
    if (detail[mm.matchmakerId]) return;
    setDetailLoading(true);
    try {
      const res = await func.get(`${urls.EVENT_EARNING_DETAIL_URL}${mm.matchmakerId}`);
      if (res?.status === 200) {
        setDetail((d) => ({ ...d, [mm.matchmakerId]: res?.data?.earnings || [] }));
      } else {
        toast.error("Could not load the line items");
      }
    } catch {
      toast.error("Could not load the line items");
    } finally {
      setDetailLoading(false);
    }
  };

  const recordPayout = async () => {
    if (!reference.trim()) {
      toast.error("A payment reference is required — it is the only proof the money moved.");
      return;
    }
    setSaving(true);
    try {
      const res = await func.post(
        `${urls.EVENT_EARNINGS_PAY_URL}${payTarget.matchmakerId}/mark-paid`,
        { reference: reference.trim() }
      );
      if (res?.status === 200) {
        toast.success(`${res?.data?.marked || 0} earning(s) marked settled for ${payTarget.name || "this matchmaker"}.`);
        setPayTarget(null);
        setReference("");
        // The row leaves the pending tab, and its cached detail is now stale.
        setDetail((d) => {
          const next = { ...d };
          delete next[payTarget.matchmakerId];
          return next;
        });
        setExpanded(null);
        load(tab);
      } else {
        toast.error(res?.data?.message || "Could not record the payout");
      }
    } catch (err) {
      // 409 is the server's KYC gate — the real one. Surface its wording.
      toast.error(err?.response?.data?.message || "Could not record the payout");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = () => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      [`MatchMatrix Matchmaker Earnings — ${TABS.find((t) => t.key === tab)?.label}`],
      [`Generated: ${new Date().toLocaleString("en-IN")}`],
      [`Total: ${totalOwed}`],
      [],
      ["#", "Matchmaker", "UPI Handle", "KYC", "Payable", "Blocked because", "From tickets (₹)", "From client spend (₹)", "Total (₹)", "Rows", "Oldest week"],
      ...rows.map((m, i) => [
        i + 1,
        escape(m.name || "(no profile)"),
        escape(m.upiHandle || "-"),
        escape(m.kycStatus),
        m.payable ? "yes" : "no",
        escape(m.blockedReason || "-"),
        m.fromTickets,
        m.fromClientSpend,
        m.total,
        m.rows,
        escape(fmtDate(m.oldestWeek)),
      ]),
    ];
    const blob = new Blob([lines.map((r) => r.join(",")).join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `matchmaker_earnings_${tab}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const blockedCount = rows.filter((m) => !m.payable).length;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Headline: the number, then the caveat about it */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-lg p-6 mb-5 shadow text-white">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-indigo-200 mb-1">
                {tab === "pending" ? "Owed to matchmakers right now" : TABS.find((t) => t.key === tab)?.label}
              </p>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faIndianRupeeSign} className="text-2xl opacity-70" />
                {Number(totalOwed || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h1>
              <p className="text-xs text-indigo-100 mt-1.5">
                Across {rows.length} matchmaker{rows.length === 1 ? "" : "s"}
                {tab === "pending" && blockedCount > 0 && (
                  <span className="ml-1 text-amber-200 font-medium">
                    · {blockedCount} blocked and cannot be settled yet
                  </span>
                )}
              </p>
            </div>
            <button
              onClick={exportCsv}
              disabled={!rows.length}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-white text-indigo-700 font-semibold rounded-lg hover:bg-indigo-50 transition shadow disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FontAwesomeIcon icon={faFileArrowDown} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); setExpanded(null); }}
              title={t.hint}
              className={`px-4 py-2 text-sm rounded-lg font-semibold transition shadow-sm ${
                tab === t.key
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-600 hover:bg-indigo-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["", "Matchmaker", "Hosting", "Her clients' spend", "Total", "Since", "KYC", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  [...Array(6)].map((_, i) => (
                    <tr key={i} className="border-b border-gray-100 animate-pulse">
                      {[...Array(8)].map((_, j) => (
                        <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded" /></td>
                      ))}
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-16 text-center text-gray-400">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-3xl mb-3 block mx-auto opacity-20" />
                      {tab === "pending"
                        ? "Nobody is owed anything. Every accrued earning has been settled."
                        : `No ${tab} earnings.`}
                    </td>
                  </tr>
                ) : (
                  rows.map((m) => {
                    const open = expanded === m.matchmakerId;
                    return (
                      <React.Fragment key={m.matchmakerId}>
                        <tr
                          className={`border-b border-gray-100 hover:bg-gray-50 transition cursor-pointer ${open ? "bg-indigo-50/40" : ""}`}
                          onClick={() => toggle(m)}
                        >
                          <td className="px-4 py-3 text-gray-400 w-8">
                            <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-xs" />
                          </td>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-800">{m.name || <span className="text-gray-400 italic">No bureau profile</span>}</p>
                            <p className="text-xs text-gray-400 font-mono">{m.upiHandle || "no payout handle"}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {m.fromTickets > 0 ? (
                              <span className="inline-flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faTicket} className="text-indigo-400 text-xs" />
                                {inr(m.fromTickets)}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                            {m.fromClientSpend > 0 ? (
                              <span className="inline-flex items-center gap-1.5">
                                <FontAwesomeIcon icon={faUsers} className="text-violet-400 text-xs" />
                                {inr(m.fromClientSpend)}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">{inr(m.total)}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDate(m.oldestWeek)}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${KYC_CHIP[m.kycStatus] || KYC_CHIP.NONE}`}>
                              {m.kycStatus}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                            {tab === "pending" && (
                              m.payable ? (
                                <button
                                  onClick={() => { setPayTarget(m); setReference(""); }}
                                  className="px-3 py-1.5 text-xs font-semibold bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition whitespace-nowrap"
                                >
                                  Record payout
                                </button>
                              ) : (
                                // The amount stays visible; only the action is blocked,
                                // and the reason travels with it.
                                <span
                                  className="inline-flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg whitespace-nowrap"
                                  title={m.blockedReason || "Not payable"}
                                >
                                  <FontAwesomeIcon icon={faTriangleExclamation} />
                                  {m.blockedReason || "Not payable"}
                                </span>
                              )
                            )}
                          </td>
                        </tr>

                        {open && (
                          <tr className="bg-gray-50/70 border-b border-gray-200">
                            <td colSpan={8} className="px-6 py-4">
                              {detailLoading && !detail[m.matchmakerId] ? (
                                <p className="text-xs text-gray-400">Loading line items…</p>
                              ) : (
                                <div className="overflow-x-auto">
                                  {/* Say the scope out loud. The detail is her COMPLETE ledger,
                                      so an owed row showing under the Reversed tab is correct —
                                      without this line it reads as a filtering bug. */}
                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    Every rupee, and where it came from
                                    <span className="ml-2 font-normal normal-case tracking-normal text-gray-400">
                                      her full history, not only this tab
                                    </span>
                                  </p>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-gray-500">
                                        {["Type", "Paid by", "Event", "They paid", "Her rate", "She earns", "Status", "When"].map((h) => (
                                          <th key={h} className="py-1.5 pr-4 font-medium text-left whitespace-nowrap">{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(detail[m.matchmakerId] || []).map((e) => (
                                        <tr key={e.id} className="border-t border-gray-200/70">
                                          <td className="py-2 pr-4 whitespace-nowrap">
                                            <span className={`px-2 py-0.5 rounded-full font-medium ${
                                              e.type === "ticket_split"
                                                ? "bg-indigo-100 text-indigo-700"
                                                : "bg-violet-100 text-violet-700"
                                            }`}>
                                              {e.type === "ticket_split" ? "Hosted a Meet" : "Her client spent"}
                                            </span>
                                          </td>
                                          <td className="py-2 pr-4 text-gray-700">{e.client || "—"}</td>
                                          <td className="py-2 pr-4 text-gray-500">{e.event || "—"}</td>
                                          {/* The arithmetic, shown rather than asserted */}
                                          <td className="py-2 pr-4 text-gray-600 whitespace-nowrap">{inr(e.basisAmount)}</td>
                                          <td className="py-2 pr-4 text-gray-500 whitespace-nowrap">{e.ratePct}%</td>
                                          <td className="py-2 pr-4 font-semibold text-gray-900 whitespace-nowrap">{inr(e.amount)}</td>
                                          <td className="py-2 pr-4 whitespace-nowrap">
                                            {e.status === "reversed" ? (
                                              <span className="text-red-600 inline-flex items-center gap-1" title={e.reversedReason || ""}>
                                                <FontAwesomeIcon icon={faRotateLeft} />
                                                Reversed
                                              </span>
                                            ) : e.status === "paid" ? (
                                              <span className="text-green-700" title={e.payoutReference ? `Ref ${e.payoutReference}` : ""}>
                                                Paid{e.payoutReference ? ` · ${e.payoutReference}` : ""}
                                              </span>
                                            ) : (
                                              <span className="text-gray-500">Owed</span>
                                            )}
                                          </td>
                                          <td className="py-2 pr-4 text-gray-400 whitespace-nowrap">{fmtDate(e.at)}</td>
                                        </tr>
                                      ))}
                                      {(detail[m.matchmakerId] || []).length === 0 && (
                                        <tr><td colSpan={8} className="py-3 text-gray-400">No line items.</td></tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          Earnings accrue automatically from settled payments, and reverse on their own if a payment is
          refunded or fails. Nothing here creates money — it records what already happened.
        </p>
      </div>

      {/* Record payout */}
      {payTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800">Record a payout</h3>
              <button onClick={() => setPayTarget(null)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-gray-800">{payTarget.name || "This matchmaker"}</p>
                <p className="text-xs text-gray-500 font-mono mt-0.5">{payTarget.upiHandle}</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{inr(payTarget.total)}</p>
                <p className="text-xs text-gray-500">
                  {payTarget.rows} earning{payTarget.rows === 1 ? "" : "s"}, oldest from {fmtDate(payTarget.oldestWeek)}
                </p>
              </div>

              {/* Said plainly, because the alternative is money marked settled and never sent. */}
              <div className="flex gap-2 text-xs text-amber-800 bg-amber-50 rounded-lg p-3">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5" />
                <p>
                  This records a transfer you have <strong>already made</strong>. It does not send money.
                  Send the {inr(payTarget.total)} first, then enter the reference below.
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Payment reference <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  maxLength={80}
                  autoFocus
                  placeholder="UPI / NEFT reference number"
                  className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Stored on every settled row, so she can be told exactly what paid her.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
              <button
                onClick={() => setPayTarget(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition"
              >
                Cancel
              </button>
              <button
                onClick={recordPayout}
                disabled={saving || !reference.trim()}
                className="px-4 py-2 text-sm bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving ? "Recording…" : `Mark ${inr(payTarget.total)} settled`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchmakerEarnings;
